import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Users, TrendingUp, BookOpen, Lightbulb, Trophy, LayoutDashboard, LogOut, Settings, Sparkles, UserPlus, Command, Layers, Mail, Map } from "lucide-react";
import { UserAvatar } from "@/components/UserAvatar";

export const NAV_ITEMS = [
  { label: "Command", path: "", icon: LayoutDashboard },
  { label: "Accounts", path: "accounts", icon: Users },
  { label: "Market", path: "market", icon: Map },
  { label: "Pipeline", path: "pipeline", icon: TrendingUp },
  { label: "Content", path: "content", icon: BookOpen },
  { label: "Advisor", path: "advisor", icon: Lightbulb },
  { label: "Campaigns", path: "campaigns", icon: Mail },
  { label: "Goals", path: "goals", icon: Trophy },
  { label: "Team", path: "team", icon: UserPlus },
];

const Logo = () => (
  <motion.div className="flex items-center gap-2 mr-2 flex-shrink-0" whileHover={{ scale: 1.02 }}>
    <motion.div className="p-2 rounded-xl bg-gradient-to-br from-ism-teal/20 to-ism-teal/10 border border-ism-teal/20"
      animate={{ boxShadow: ["0 0 10px rgba(0,163,138,0.2)", "0 0 20px rgba(0,163,138,0.3)", "0 0 10px rgba(0,163,138,0.2)"] }}
      transition={{ duration: 2, repeat: Infinity }}>
      <Command className="h-5 w-5 text-ism-teal" />
    </motion.div>
    <span className="font-bold text-lg neon-text tracking-tight hidden lg:block">ISM</span>
  </motion.div>
);

const NavButton = ({ item, idx, isActive, onClick }) => {
  const Icon = item.icon;
  return (
    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}>
      <Button onClick={onClick} variant="ghost" data-testid={`nav-${item.label.toLowerCase()}`}
        title={item.label} className={`relative px-1.5 py-2 text-sm rounded-xl transition-all flex-shrink-0 ${isActive ? "bg-ism-teal/10 border border-ism-teal/30 text-ism-teal" : "border border-transparent text-gray-400 hover:text-white hover:bg-white/5"}`}>
        <Icon className={`h-4 w-4 2xl:mr-2 ${isActive ? "text-ism-teal" : "text-gray-500"}`} />
        <span className="hidden 2xl:inline">{item.label}</span>
        {isActive && <motion.div className="absolute -bottom-[13px] left-1/2 -translate-x-1/2 w-8 h-0.5 bg-ism-teal rounded-full" layoutId="activeTab" />}
      </Button>
    </motion.div>
  );
};

const HeaderButton = ({ onClick, title, testId, className = "border-white/10", children }) => (
  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
    <Button onClick={onClick} variant="ghost" title={title} data-testid={testId} className={`border text-gray-400 hover:text-white hover:bg-white/5 rounded-xl ${className}`}>
      {children}
    </Button>
  </motion.div>
);

// Sticky top bar: logo, section navigation, workspace toggle, avatar, setup/settings and logout.
export const AppHeader = ({ userId, currentPath, isNewUser, currentInstance, showInstanceSwitcher, onToggleInstanceSwitcher, onSetup, onLogout, navigate }) => (
  <header className="border-b border-white/5 bg-black/80 sticky top-0 z-50">
    <div className="container mx-auto px-6 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-0.5 min-w-0 flex-1 overflow-x-auto no-scrollbar nav-scroll-hint" data-testid="nav-items">
          <Logo />
          {NAV_ITEMS.map((item, idx) => (
            <NavButton key={item.path} item={item} idx={idx} isActive={currentPath === item.path} onClick={() => navigate(`/dashboard/${userId}/${item.path}`)} />
          ))}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0 ml-1">
          <HeaderButton onClick={onToggleInstanceSwitcher} title="Switch Workspace" testId="toggle-instance-switcher"
            className={showInstanceSwitcher ? "border-ism-teal/50 bg-ism-teal/10" : "border-white/10"}>
            <Layers className={`h-4 w-4 mr-2 ${showInstanceSwitcher ? "text-ism-teal" : ""}`} />
            <span className="hidden sm:inline max-w-[84px] truncate">{currentInstance?.name || "Workspaces"}</span>
          </HeaderButton>

          {!isNewUser && (
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}><UserAvatar userId={userId} /></motion.div>
          )}

          <HeaderButton onClick={onSetup} title={isNewUser ? "Setup New Sales Machine" : "Targeting Settings"} testId="setup-settings-btn">
            {isNewUser ? <Sparkles className="h-4 w-4 mr-2 text-ism-teal" /> : <Settings className="h-4 w-4 mr-2 text-ism-teal" />}
            <span className="hidden sm:inline">{isNewUser ? "Setup" : "Settings"}</span>
          </HeaderButton>

          <HeaderButton onClick={onLogout} testId="logout-btn">
            <LogOut className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Logout</span>
          </HeaderButton>
        </div>
      </div>
    </div>
  </header>
);

export const LoadingScreen = () => (
  <div className="min-h-screen gradient-bg flex items-center justify-center">
    <div className="text-center">
      <div className="p-4 rounded-xl bg-ism-teal/10 border border-ism-teal/20 inline-block mb-4">
        <Command className="h-8 w-8 text-ism-teal animate-pulse" />
      </div>
      <p className="text-gray-400 text-sm">Loading...</p>
    </div>
  </div>
);
